import { APIService } from "@/services/api";
import { DepartmentBasedAssignmentsService } from "@/services/departmentBasedAssignmentsService";
import { RoleBasedAssignmentsService } from "@/services/roleBasedAssignmentsService";
import {
  createContext,
  PropsWithChildren,
  useContext,
  useMemo,
  useState,
} from "react";
import { ReadContractorAssignee } from "../types";

interface ContractorsAssignmentsContextType {
  isAddContractorDialogOpen: boolean;
  setAddContractorDialogOpen: (value: boolean) => void;
  isRoleBasedProject: boolean;
  ProjectContractorsService: APIService<ReadContractorAssignee>;
  projectContractorsMainQueryKey: string;
}

export const ContractorsAssignmentsContext = createContext<
  ContractorsAssignmentsContextType | undefined
>(undefined);

interface ContractorsAssignmentsContextProviderProps extends PropsWithChildren {
  isRoleBasedProject?: boolean;
}

export const ContractorsAssignmentsContextProvider = ({
  children,
  isRoleBasedProject = false,
}: ContractorsAssignmentsContextProviderProps) => {
  const projectContractorsMainQueryKey = isRoleBasedProject
    ? "roleBasedAssignments"
    : "departmentBasedAssignments";

  const [isAddContractorDialogOpen, setAddContractorDialogOpen] =
    useState(false);

  const ProjectContractorsService = useMemo(
    () =>
      isRoleBasedProject
        ? RoleBasedAssignmentsService
        : DepartmentBasedAssignmentsService,
    [isRoleBasedProject],
  );

  const value: ContractorsAssignmentsContextType = useMemo(
    () => ({
      isRoleBasedProject,
      isAddContractorDialogOpen,
      setAddContractorDialogOpen,
      ProjectContractorsService,
      projectContractorsMainQueryKey,
    }),
    [
      isRoleBasedProject,
      isAddContractorDialogOpen,
      setAddContractorDialogOpen,
      ProjectContractorsService,
      projectContractorsMainQueryKey,
    ],
  );

  return (
    <ContractorsAssignmentsContext.Provider value={value}>
      {children}
    </ContractorsAssignmentsContext.Provider>
  );
};

export const useContractorsAssignmentsContext = () => {
  const context = useContext(ContractorsAssignmentsContext);
  if (!context) {
    throw new Error(
      "useContractorsAssignmentsContext must be used within a ContractorsAssignmentsContextProvider",
    );
  }
  return context;
};
